// TikTok funnel events for Okazyo.
//
// cart-pricing.js owns the pixel and fires InitiateCheckout on the cart pay
// button. This file fills the two holes around it:
//
//   ViewContent  - on a product page
//   AddToCart    - on every /cart/add, whichever button caused it
//   PrefillStarted / SubmitForm - the on-site first checkout step
//
// It also renders that first step: a single screen that collects the delivery
// details the affiliate checkout would otherwise ask for, so the customer is
// handed over already filled in.

(function () {
  'use strict';

  var STORAGE_KEY = 'okz_prefill';
  var queue = [];
  var ready = false;

  function pixelReady() {
    return window.ttq && typeof window.ttq.track === 'function';
  }

  function flush() {
    if (!pixelReady()) return;
    ready = true;
    while (queue.length) {
      var item = queue.shift();
      try {
        window.ttq.track(item[0], item[1]);
      } catch (e) {}
    }
  }

  function track(name, props) {
    if (ready && pixelReady()) {
      try {
        window.ttq.track(name, props);
      } catch (e) {}
      return;
    }
    if (pixelReady()) {
      ready = true;
      try {
        window.ttq.track(name, props);
      } catch (e) {}
      return;
    }
    queue.push([name, props]);
  }

  var attempts = 0;
  var poll = setInterval(function () {
    if (pixelReady()) {
      flush();
      clearInterval(poll);
    } else if (++attempts > 150) {
      clearInterval(poll);
    }
  }, 100);

  function currency() {
    return (
      (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) ||
      'EUR'
    );
  }

  function money(cents) {
    return Math.round(cents || 0) / 100;
  }

  // ── ViewContent ────────────────────────────────────────────────────────────
  function fireViewContent() {
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
    var product = meta && meta.product;
    if (!product) return;
    var variant = (product.variants && product.variants[0]) || {};
    track('ViewContent', {
      content_type: 'product',
      content_id: String(product.id),
      content_name: product.title || document.title,
      contents: [
        {
          content_id: String(product.id),
          content_name: product.title || document.title,
          quantity: 1,
          price: money(variant.price),
        },
      ],
      value: money(variant.price),
      currency: currency(),
    });
  }

  // ── AddToCart ──────────────────────────────────────────────────────────────
  // Patching fetch catches the product form, the sticky bar, quick-add and any
  // upsell: they all POST to Theme.routes.cart_add_url.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);
      var result = nativeFetch.apply(this, arguments);
      if (!isAdd) return result;

      return result.then(function (response) {
        try {
          if (response && response.ok) {
            response
              .clone()
              .json()
              .then(function (data) {
                var items = data && data.items ? data.items : [data];
                if (!items || !items.length || !items[0]) return;
                var total = 0;
                var contents = [];
                items.forEach(function (item) {
                  var line =
                    item.final_line_price != null
                      ? item.final_line_price
                      : (item.price || 0) * (item.quantity || 1);
                  total += line;
                  contents.push({
                    content_id: String(item.product_id || item.id),
                    content_name: item.product_title || item.title || '',
                    quantity: item.quantity || 1,
                    price: money(item.price),
                  });
                });
                track('AddToCart', {
                  content_type: 'product',
                  content_ids: contents.map(function (c) {
                    return c.content_id;
                  }),
                  contents: contents,
                  value: money(total),
                  currency: currency(),
                });
              })
              .catch(function () {});
          }
        } catch (e) {}
        return response;
      });
    };
  }

  // ── The on-site first step ─────────────────────────────────────────────────
  var LANG = (document.documentElement.lang || 'fr').slice(0, 2).toLowerCase();
  var T = LANG === 'en'
    ? {
        title: 'Where should we deliver?',
        sub: 'Your details are passed to the secure payment. You will not have to type them again.',
        submit: 'Continue to payment',
        redirect: 'Redirecting…',
        errorFields: 'Please complete the highlighted fields.',
        errorRequired: 'These details are needed to continue to payment.',
        labels: {
          country: 'Country', state: 'State', zip: 'ZIP code', city: 'City',
          address: 'Address', first: 'First name', last: 'Last name',
          email: 'Email', phone: 'Phone (optional)',
        },
      }
    : {
        title: 'Où livrons-nous ?',
        sub: 'Vos informations sont transmises au paiement sécurisé. Vous n\'aurez rien à ressaisir.',
        submit: 'Continuer vers le paiement',
        redirect: 'Redirection…',
        errorFields: 'Merci de compléter les champs en rouge.',
        errorRequired: 'Ces informations sont nécessaires pour poursuivre vers le paiement.',
        labels: {
          country: 'Pays', state: 'État / Région', zip: 'Code postal', city: 'Ville',
          address: 'Adresse', first: 'Prénom', last: 'Nom',
          email: 'E-mail', phone: 'Téléphone (facultatif)',
        },
      };

  // Countries the funnel accepts: EU-27 plus US, UK, CH, NO, CA.
  var COUNTRIES = [
    ['FR', 'France'], ['DE', 'Allemagne'], ['AT', 'Autriche'], ['BE', 'Belgique'],
    ['BG', 'Bulgarie'], ['HR', 'Croatie'], ['CY', 'Chypre'], ['CZ', 'Tchéquie'],
    ['DK', 'Danemark'], ['EE', 'Estonie'], ['FI', 'Finlande'], ['GR', 'Grèce'],
    ['HU', 'Hongrie'], ['IE', 'Irlande'], ['IT', 'Italie'], ['LV', 'Lettonie'],
    ['LT', 'Lituanie'], ['LU', 'Luxembourg'], ['MT', 'Malte'], ['NL', 'Pays-Bas'],
    ['PL', 'Pologne'], ['PT', 'Portugal'], ['RO', 'Roumanie'], ['SK', 'Slovaquie'],
    ['SI', 'Slovénie'], ['ES', 'Espagne'], ['SE', 'Suède'], ['US', 'États-Unis'],
    ['GB', 'Royaume-Uni'], ['CH', 'Suisse'], ['NO', 'Norvège'], ['CA', 'Canada'],
  ];
  var COUNTRIES_EN = {
    FR: 'France', DE: 'Germany', AT: 'Austria', BE: 'Belgium', BG: 'Bulgaria',
    HR: 'Croatia', CY: 'Cyprus', CZ: 'Czechia', DK: 'Denmark', EE: 'Estonia',
    FI: 'Finland', GR: 'Greece', HU: 'Hungary', IE: 'Ireland', IT: 'Italy',
    LV: 'Latvia', LT: 'Lithuania', LU: 'Luxembourg', MT: 'Malta',
    NL: 'Netherlands', PL: 'Poland', PT: 'Portugal', RO: 'Romania',
    SK: 'Slovakia', SI: 'Slovenia', ES: 'Spain', SE: 'Sweden',
    US: 'United States', GB: 'United Kingdom', CH: 'Switzerland',
    NO: 'Norway', CA: 'Canada',
  };

  // Countries that need a state / province before the partner checkout accepts
  // the address.
  var NEEDS_STATE = ['US', 'CA'];

  var FIELDS = [
    { name: 'first', autocomplete: 'given-name', type: 'text' },
    { name: 'last', autocomplete: 'family-name', type: 'text' },
    { name: 'email', autocomplete: 'email', type: 'email' },
    { name: 'phone', autocomplete: 'tel', type: 'tel' },
    { name: 'address', autocomplete: 'street-address', type: 'text', wide: true },
    { name: 'zip', autocomplete: 'postal-code', type: 'text' },
    { name: 'city', autocomplete: 'address-level2', type: 'text' },
  ];

  var REQUIRED = ['country', 'first', 'last', 'email', 'address', 'zip', 'city'];

  function defaultCountry() {
    try {
      if (window.Shopify && window.Shopify.country) return String(window.Shopify.country).toUpperCase();
    } catch (e) {}
    return 'FR';
  }

  function loadSaved() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function save(values) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
    } catch (e) {}
  }

  // Creates (or matches) a Shopify customer from the step's data. Fire and
  // forget: it must never delay or block the redirect to the partner
  // checkout. No marketing consent is given here — that needs its own opt-in.
  function createCustomer(values) {
    try {
      var body = new URLSearchParams();
      body.set('form_type', 'customer');
      body.set('utf8', '\u2713');
      body.set('contact[email]', values.email || '');
      body.set('contact[first_name]', values.first || '');
      body.set('contact[last_name]', values.last || '');
      body.set('contact[phone]', values.phone || '');
      body.set('contact[address1]', values.address || '');
      body.set('contact[city]', values.city || '');
      body.set('contact[zip]', values.zip || '');
      body.set('contact[country]', values.country || '');
      body.set('contact[province]', values.state || '');
      body.set('contact[tags]', 'prefill-checkout,panier-abandonne');
      window.fetch('/contact', {
        method: 'POST',
        body: body.toString(),
        redirect: 'manual',
        keepalive: true,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
      }).catch(function () {});
    } catch (e) {}
  }

  var LEAD_ENDPOINT = 'https://okazyo-leads.vercel.app/api/lead';

  function storeId() {
    try {
      if (window.Shopify && window.Shopify.shop) {
        return String(window.Shopify.shop).replace('.myshopify.com', '');
      }
    } catch (e) {}
    return location.hostname.replace(/^www\./, '');
  }

  // Copies the step's data to our own lead store (encrypted at rest). Fire and
  // forget, no-cors, keepalive: it must never delay the redirect.
  function sendLead(values, context) {
    try {
      var cart = (context && context.cart) || {};
      var totals = (context && context.totals) || {};
      var items = cart.items || [];
      var product = items.length
        ? items.map(function (item) {
            return (item.quantity || 1) + 'x ' + (item.product_title || item.title || '');
          }).join(' | ')
        : '';
      var payload = {
        store: storeId(),
        first: values.first,
        last: values.last,
        email: values.email,
        phone: values.phone,
        address: values.address,
        zip: values.zip,
        city: values.city,
        country: values.country,
        state: values.state,
        product: product,
        items: String(cart.item_count || items.length || ''),
        total: totals.tierTotal ? (totals.tierTotal / 100).toFixed(2) : '',
        tier: totals.tierTotal ? String(totals.tierTotal) : '',
        landing: location.href.slice(0, 400),
        referrer: (document.referrer || '').slice(0, 400),
        locale: (document.documentElement.lang || '').slice(0, 20),
      };
      window.fetch(LEAD_ENDPOINT, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      }).catch(function () {});
    } catch (e) {}
  }

  function buildModal() {
    var overlay = document.createElement('dialog');
    overlay.className = 'okz-prefill';
    overlay.setAttribute('aria-labelledby', 'okz-prefill-title');

    var fields = FIELDS.map(function (field) {
      return (
        '<label class="okz-prefill__field' +
        (field.wide ? ' okz-prefill__field--wide' : '') +
        '">' +
        '<span>' +
        T.labels[field.name] +
        '</span>' +
        '<input type="' +
        field.type +
        '" name="' +
        field.name +
        '" autocomplete="' +
        field.autocomplete +
        '" inputmode="' +
        (field.name === 'email' ? 'email' : field.name === 'phone' ? 'tel' : 'text') +
        '">' +
        '</label>'
      );
    }).join('');

    var countryOptions = COUNTRIES.map(function (country) {
      var name = LANG === 'en' ? COUNTRIES_EN[country[0]] : country[1];
      return '<option value="' + country[0] + '">' + name + '</option>';
    }).join('');

    var countryField =
      '<label class="okz-prefill__field okz-prefill__field--wide">' +
      '<span>' + T.labels.country + '</span>' +
      '<select name="country" autocomplete="country">' + countryOptions + '</select>' +
      '</label>';

    var stateField =
      '<label class="okz-prefill__field okz-prefill__field--wide" data-state-field hidden>' +
      '<span>' + T.labels.state + '</span>' +
      '<input type="text" name="state" autocomplete="address-level1" maxlength="60">' +
      '</label>';

    overlay.innerHTML =
      '<div class="okz-prefill__panel">' +
      '<h2 id="okz-prefill-title" class="okz-prefill__title">' + T.title + '</h2>' +
      '<p class="okz-prefill__sub">' + T.sub + '</p>' +
      '<form class="okz-prefill__form" novalidate>' +
      '<div class="okz-prefill__grid">' +
      countryField +
      fields +
      stateField +
      '</div>' +
      '<p class="okz-prefill__error" hidden>' + T.errorFields + '</p>' +
      '<div class="okz-prefill__actions">' +
      '<button type="submit" class="okz-prefill__submit">' + T.submit + '</button>' +
      '</div>' +
      '</form>' +
      '</div>';

    return overlay;
  }

  var stylesInjected = false;
  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;
    var style = document.createElement('style');
    style.textContent = [
      '.okz-prefill{position:fixed;inset:0;width:100%;height:100%;max-width:100%;max-height:100%;margin:0;padding:0;border:0;background:transparent;overflow:visible}',
      '.okz-prefill[open]{display:flex;align-items:flex-end;justify-content:center}',
      '.okz-prefill::backdrop{background:rgba(10,10,10,.55)}',
      '@media(min-width:640px){.okz-prefill[open]{align-items:center;padding:24px}}',
      '.okz-prefill__panel{background:#fff;width:100%;max-width:520px;max-height:92vh;display:flex;flex-direction:column;overflow:hidden;border-radius:18px 18px 0 0;padding:20px 20px 0;box-shadow:0 -8px 40px rgba(0,0,0,.25);font-family:inherit}',
      '@media(min-width:640px){.okz-prefill__panel{border-radius:18px;padding-bottom:0}}',
      '.okz-prefill__title{margin:0 0 6px;font-size:20px;line-height:1.2;color:#16181B}',
      '.okz-prefill__sub{margin:0 0 14px;font-size:13px;line-height:1.4;color:#5a5f66}',
      '.okz-prefill__form{display:flex;flex-direction:column;min-height:0;flex:1}',
      '.okz-prefill__grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;overflow-y:auto;min-height:0;padding-bottom:2px}',
      '.okz-prefill__field{display:flex;flex-direction:column;gap:4px}',
      '.okz-prefill__field--wide{grid-column:1/-1}',
      '.okz-prefill__field span{font-size:12px;font-weight:600;color:#3c4147}',
      '.okz-prefill__field input,.okz-prefill__field select{height:44px;border:1px solid #d9dde2;border-radius:10px;padding:0 12px;font-size:16px;color:#16181B;background:#fff;width:100%;box-sizing:border-box}',
      '.okz-prefill__field select{appearance:none;-webkit-appearance:none;background-image:linear-gradient(45deg,transparent 50%,#6b7178 50%),linear-gradient(135deg,#6b7178 50%,transparent 50%);background-position:calc(100% - 18px) 19px,calc(100% - 13px) 19px;background-size:5px 5px,5px 5px;background-repeat:no-repeat;padding-right:34px}',
      '.okz-prefill__field[hidden]{display:none}',
      '.okz-prefill__field input:focus,.okz-prefill__field select:focus{outline:none;border-color:#16181B}',
      '.okz-prefill__field--bad input,.okz-prefill__field--bad select{border-color:#E8112D;background:#fff6f7}',
      '.okz-prefill__error{color:#E8112D;font-size:13px;margin:8px 0 0}',
      '.okz-prefill__actions{flex:none;background:#fff;padding:12px 0 10px;box-shadow:0 -6px 14px rgba(255,255,255,.9)}',
      '.okz-prefill__submit{width:100%;height:50px;border:0;border-radius:12px;background:#16181B;color:#fff;font-size:16px;font-weight:700;cursor:pointer}',
      '.okz-prefill__submit[disabled]{opacity:.6;cursor:default}',
    ].join('');
    document.head.appendChild(style);
  }

  var openPromise = null;

  function open(context) {
    if (openPromise) return openPromise;
    var ctx = context || {};
    injectStyles();

    var overlay = buildModal();
    var form = overlay.querySelector('form');
    var errorEl = overlay.querySelector('.okz-prefill__error');
    var submitBtn = overlay.querySelector('.okz-prefill__submit');
    var saved = loadSaved();
    var started = false;

    var countrySelect = form.querySelector('[name="country"]');
    var stateFieldEl = form.querySelector('[data-state-field]');
    var stateInput = form.querySelector('[name="state"]');

    function needsState(country) {
      return NEEDS_STATE.indexOf(country) !== -1;
    }

    function syncStateField() {
      var required = needsState(countrySelect.value);
      stateFieldEl.hidden = !required;
      stateInput.required = required;
      if (!required) stateInput.value = '';
      stateInput.setAttribute('placeholder', countrySelect.value === 'US' ? 'TX' : '');
    }

    countrySelect.value = saved.country || defaultCountry();
    if (!countrySelect.value) countrySelect.value = 'FR';
    syncStateField();
    if (saved.state) stateInput.value = saved.state;
    countrySelect.addEventListener('change', function () {
      countrySelect.closest('.okz-prefill__field').classList.remove('okz-prefill__field--bad');
      syncStateField();
    });

    FIELDS.forEach(function (field) {
      var input = form.querySelector('[name="' + field.name + '"]');
      if (!input) return;
      if (saved[field.name]) input.value = saved[field.name];
      input.addEventListener('focus', function () {
        if (started) return;
        started = true;
        track('PrefillStarted', { content_type: 'product' });
      });
      input.addEventListener('input', function () {
        input.closest('.okz-prefill__field').classList.remove('okz-prefill__field--bad');
      });
    });

    stateInput.addEventListener('input', function () {
      stateInput.closest('.okz-prefill__field').classList.remove('okz-prefill__field--bad');
    });

    function collect() {
      var values = { country: countrySelect.value, state: stateInput.value.trim() };
      FIELDS.forEach(function (field) {
        var input = form.querySelector('[name="' + field.name + '"]');
        values[field.name] = input ? input.value.trim() : '';
      });
      return values;
    }

    function close(result) {
      try {
        if (overlay.open) overlay.close();
      } catch (e) {}
      overlay.remove();
      openPromise = null;
      document.documentElement.style.removeProperty('overflow');
      resolve(result);
    }

    var resolve;
    openPromise = new Promise(function (res) {
      resolve = res;
    });

    function missing(values) {
      var required = REQUIRED.slice();
      if (needsState(values.country)) required.push('state');
      return required.filter(function (name) {
        return !values[name];
      });
    }

    function validEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value);
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var values = collect();
      var bad = missing(values);
      if (values.email && !validEmail(values.email)) bad.push('email');

      form.querySelectorAll('.okz-prefill__field').forEach(function (el) {
        el.classList.remove('okz-prefill__field--bad');
      });
      bad.forEach(function (name) {
        var input = form.querySelector('[name="' + name + '"]');
        if (input) input.closest('.okz-prefill__field').classList.add('okz-prefill__field--bad');
      });

      if (bad.length) {
        errorEl.textContent = T.errorFields;
        errorEl.hidden = false;
        return;
      }
      errorEl.hidden = true;
      submitBtn.disabled = true;
      submitBtn.textContent = T.redirect;
      save(values);
      createCustomer(values);
      sendLead(values, ctx);
      track('SubmitForm', {
        content_type: 'product',
        content_name: 'prefill_checkout',
      });
      close(values);
    });

    overlay.addEventListener('cancel', function (event) {
      event.preventDefault();
      errorEl.hidden = false;
      errorEl.textContent = T.errorRequired;
    });

    document.body.appendChild(overlay);
    document.documentElement.style.overflow = 'hidden';
    if (typeof overlay.showModal === 'function') {
      overlay.showModal();
    } else {
      overlay.setAttribute('open', '');
    }
    var firstInput = form.querySelector('input');
    if (firstInput) firstInput.focus({ preventScroll: true });

    return openPromise;
  }

  window.okzPrefillStep = { open: open };
  window.okzTT = { track: track };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fireViewContent);
  } else {
    fireViewContent();
  }
})();
